import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { ReactFlow, MiniMap, Controls, Background, applyNodeChanges, applyEdgeChanges, addEdge, reconnectEdge } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import useCanvasStore from '@/store/canvasStore';
import useUiStore from '@/store/uiStore';
import AtlasNode from '@/components/canvas/AtlasNode';
import AtlasEdge from '@/components/canvas/AtlasEdge';
import ExplainTab from '@/components/sidebar/ExplainTab';
import AskTab from '@/components/sidebar/AskTab';
import QuizTab from '@/components/sidebar/QuizTab';
import Onboarding from '@/components/Onboarding';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { ArrowLeft, Save, BookOpen, Pencil, X,
  Search, MessagesSquare, GraduationCap, Paintbrush, Trash2, GitBranch,
  Download, FileImage, FileText, Lock, Crown, Share2, Users
} from 'lucide-react';
import ShareModal from '@/components/collab/ShareModal';
import CollabCursors from '@/components/collab/CollabCursors';
import CollaboratorAvatars from '@/components/collab/CollaboratorAvatars';
import useCollaboration from '@/hooks/useCollaboration';

// Canvas onboarding steps - initial steps (before node selection)
const CANVAS_ONBOARDING_STEPS = [
  {
    target: '[data-testid="mode-toggle"]',
    title: 'Edit & Learn Modes',
    description: 'Switch between Edit mode to modify your mind map structure, and Learn mode to explore AI-powered features for each topic.',
    position: 'bottom'
  },
  {
    target: '[data-testid="canvas-area"]',
    title: 'Your Mind Map Canvas',
    description: 'Click on any node to select it. In Edit mode, use the + button to add child nodes. Drag nodes to rearrange your map.',
    position: 'left'
  },
  {
    target: '[data-testid="save-btn"]',
    title: 'Save Your Work',
    description: 'Your map auto-saves, but you can also manually save anytime. PRO users can export to PDF and PNG!',
    position: 'bottom'
  }
];

const BG_COLORS = [
  { name: 'White', value: '#ffffff' },
  { name: 'Paper', value: '#FDFCF8' },
  { name: 'Slate', value: '#f8fafc' },
  { name: 'Warm', value: '#fefce8' },
  { name: 'Mint', value: '#f0fdf4' },
  { name: 'Sky', value: '#f0f9ff' },
];

const EDGE_COLORS = [
  { name: 'Cobalt', value: '#0047AB' },
  { name: 'Orange', value: '#FF4F00' },
  { name: 'Emerald', value: '#10B981' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Violet', value: '#8B5CF6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Red', value: '#EF4444' },
  { name: 'Gray', value: '#6B7280' },
  { name: 'Slate', value: '#475569' },
  { name: 'Lime', value: '#84CC16' },
  { name: 'Indigo', value: '#6366F1' },
];

export default function CanvasPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser } = useAuth();
  const { openUpgradeModal } = useUiStore();
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('saved'); // 'saved' | 'saving' | 'error'
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showSidebarOnboarding, setShowSidebarOnboarding] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const settingsPanelRef = useRef(null);
  const settingsButtonRef = useRef(null);
  const lastCursorUpdate = useRef(0);
  const isReceivingCollabUpdate = useRef(false);

  const {
    mapId, mapTitle, mode, nodes, edges, selectedNodeId, selectedEdgeId, sidebarTab,
    nodeChats, nodeMastery, isDirty, canvasBackground,
    setNodes, setEdges, setSelectedNodeId, setSelectedEdgeId,
    setSidebarTab, closeSidebar, loadMap, resetMap, setDirty,
    deleteEdge, updateEdgeColor, setMapTitle, setCanvasBackground,
    updateNodePosition, updateNodeLabel, setNodesAndEdges
  } = useCanvasStore();

  // Real-time collaboration - owner mode
  const {
    participants,
    cursors,
    isConnected: collabConnected,
    remoteNodeDrag,
    remoteTextEdit,
    sendCursorPosition,
    sendNodeDrag,
    sendNodeDragEnd,
    sendTextEdit,
    sendMapUpdate
  } = useCollaboration(mapId, token, true);

  const nodeTypes = useMemo(() => ({ atlasNode: AtlasNode }), []);
  const edgeTypes = useMemo(() => ({ default: AtlasEdge }), []);
  const loaded = useRef(false);

  // Show onboarding after canvas is loaded
  useEffect(() => {
    if (nodes.length > 0) {
      const timer = setTimeout(() => setShowOnboarding(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [nodes.length]);

  // Show sidebar onboarding when user first clicks a node and sees the sidebar
  useEffect(() => {
    if (selectedNodeId && sidebarTab && user && !user?.onboarding?.canvas_sidebar) {
      const timer = setTimeout(() => setShowSidebarOnboarding(true), 500);
      return () => clearTimeout(timer);
    }
  }, [selectedNodeId, sidebarTab, user]);

  // Close settings panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showSettingsPanel && 
          settingsPanelRef.current && 
          !settingsPanelRef.current.contains(event.target) &&
          settingsButtonRef.current &&
          !settingsButtonRef.current.contains(event.target)) {
        setShowSettingsPanel(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettingsPanel]);

  // Auto-save with debounce
  useEffect(() => {
    if (!isDirty) return;
    
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    // Set new timer for auto-save (3 seconds debounce)
    autoSaveTimerRef.current = setTimeout(async () => {
      const state = useCanvasStore.getState();
      if (!state.isDirty) return;
      
      setAutoSaveStatus('saving');
      try {
        const data = {
          title: state.mapTitle || 'Untitled Map',
          nodes: JSON.stringify(state.nodes),
          edges: JSON.stringify(state.edges),
          nodeChats: JSON.stringify(state.nodeChats),
          nodeMastery: JSON.stringify(state.nodeMastery),
        };
        
        if (state.mapId) {
          await api.put(`/maps/${state.mapId}`, data);
        } else {
          const res = await api.post('/maps', data);
          loadMap(res.data.id, state.mapTitle, state.nodes, state.edges, state.nodeChats, state.nodeMastery);
        }
        setDirty(false);
        setAutoSaveStatus('saved');
      } catch (err) {
        console.error('Auto-save failed:', err);
        setAutoSaveStatus('error');
      }
    }, 3000);
    
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [isDirty, loadMap, setDirty]);

  // Broadcast map updates to collaborators when nodes/edges change
  const pendingCollabUpdate = useRef(null);
  useEffect(() => {
    // Skip if not connected, no mapId, or receiving update from collaborator
    if (!collabConnected || !mapId || isReceivingCollabUpdate.current) return;
    if (nodes.length === 0) return;
    
    // Debounce collaboration updates
    if (pendingCollabUpdate.current) {
      clearTimeout(pendingCollabUpdate.current);
    }
    pendingCollabUpdate.current = setTimeout(() => {
      if (!isReceivingCollabUpdate.current) {
        console.log('Owner broadcasting map update:', nodes.length, 'nodes');
        sendMapUpdate(nodes, edges);
      }
    }, 200);
    
    return () => {
      if (pendingCollabUpdate.current) {
        clearTimeout(pendingCollabUpdate.current);
      }
    };
  }, [nodes, edges, collabConnected, mapId, sendMapUpdate]);

  // Listen for map updates from collaborators
  useEffect(() => {
    window.__collabMapUpdate = (newNodes, newEdges) => {
      if (newNodes && newEdges) {
        console.log('Owner received map update from collaborator:', newNodes.length, 'nodes');
        isReceivingCollabUpdate.current = true;
        setNodesAndEdges(newNodes, newEdges);
        // Reset flag after a short delay
        setTimeout(() => {
          isReceivingCollabUpdate.current = false;
        }, 100);
      }
    };
    
    // Expose sendTextEdit for components to use
    window.__collabSendTextEdit = sendTextEdit;
    
    return () => {
      window.__collabMapUpdate = null;
      window.__collabSendTextEdit = null;
    };
  }, [setNodesAndEdges, sendTextEdit]);

  // Apply remote node drag position in real-time
  useEffect(() => {
    if (!remoteNodeDrag) return;
    
    const { nodeId, position } = remoteNodeDrag;
    console.log('Applying remote node drag:', nodeId, position);
    updateNodePosition(nodeId, position);
  }, [remoteNodeDrag, updateNodePosition]);

  // Apply remote text edit in real-time
  useEffect(() => {
    if (!remoteTextEdit) return;
    
    const { nodeId, label } = remoteTextEdit;
    console.log('Applying remote text edit:', nodeId, label);
    updateNodeLabel(nodeId, label);
  }, [remoteTextEdit, updateNodeLabel]);

  // Handle cursor movement for collaboration
  const handleCollabMouseMove = useCallback((e) => {
    if (!collabConnected) return;
    const now = Date.now();
    if (now - lastCursorUpdate.current < 50) return;
    lastCursorUpdate.current = now;
    sendCursorPosition(e.clientX, e.clientY);
  }, [collabConnected, sendCursorPosition]);

  // Handle node drag - broadcast position in real-time
  const handleCollabNodeDrag = useCallback((event, node) => {
    if (!collabConnected) return;
    sendNodeDrag(node.id, node.position);
  }, [collabConnected, sendNodeDrag]);

  // Handle node drag stop - persist and broadcast final state
  const handleCollabNodeDragStop = useCallback((event, node) => {
    if (!collabConnected) return;
    // Update the node position in our nodes array and broadcast
    const updatedNodes = nodes.map(n => 
      n.id === node.id ? { ...n, position: node.position } : n
    );
    sendNodeDragEnd(updatedNodes, edges);
  }, [collabConnected, nodes, edges, sendNodeDragEnd]);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    const isGenerated = searchParams.get('generated') === 'true';
    const mapIdParam = searchParams.get('mapId');
    if (isGenerated) {
      const data = sessionStorage.getItem('atlas_generated_map');
      if (data) {
        const parsed = JSON.parse(data);
        loadMap(null, parsed.title, parsed.nodes, parsed.edges, {}, {});
        sessionStorage.removeItem('atlas_generated_map');
      }
    } else if (mapIdParam) {
      loadMapFromApi(mapIdParam);
    } else {
      const defaultNode = {
        id: 'root', type: 'atlasNode', position: { x: 0, y: 0 },
        data: { label: 'Start Here', description: 'Click me, then use + to add child nodes', mastery: 'none', nodeType: 'parent' }
      };
      loadMap(null, 'Untitled Map', [defaultNode], [], {}, {});
    }
    // eslint-disable-next-line
  }, []);

  const loadMapFromApi = async (id) => {
    try {
      const res = await api.get(`/maps/${id}`);
      const m = res.data;
      loadMap(m.id, m.title, JSON.parse(m.nodes || '[]'), JSON.parse(m.edges || '[]'),
        JSON.parse(m.nodeChats || '{}'), JSON.parse(m.nodeMastery || '{}'));
    } catch { toast.error('Failed to load map'); }
  };

  const onNodesChange = useCallback((changes) => {
    const currentMode = useCanvasStore.getState().mode;
    if (currentMode === 'learn') {
      // In learn mode, only allow position changes (dragging)
      const filtered = changes.filter(c => c.type === 'position' || c.type === 'dimensions' || c.type === 'select');
      if (filtered.length === 0) return;
      setNodes(applyNodeChanges(filtered, useCanvasStore.getState().nodes));
    } else {
      setNodes(applyNodeChanges(changes, useCanvasStore.getState().nodes));
    }
  }, [setNodes]);

  const onEdgesChange = useCallback((changes) => {
    if (useCanvasStore.getState().mode === 'learn') {
      const filtered = changes.filter(c => c.type === 'select');
      if (filtered.length === 0) return;
      setEdges(applyEdgeChanges(filtered, useCanvasStore.getState().edges));
      return;
    }
    setEdges(applyEdgeChanges(changes, useCanvasStore.getState().edges));
  }, [setEdges]);

  const onConnect = useCallback((params) => {
    if (useCanvasStore.getState().mode === 'learn') return;
    setEdges(addEdge({ ...params, style: { stroke: '#0047AB' } }, useCanvasStore.getState().edges));
  }, [setEdges]);

  const onReconnect = useCallback((oldEdge, newConnection) => {
    if (useCanvasStore.getState().mode === 'learn') return;
    setEdges(reconnectEdge(oldEdge, newConnection, useCanvasStore.getState().edges));
  }, [setEdges]);

  const onNodeClick = useCallback((_, node) => {
    setSelectedNodeId(node.id);
    if (useCanvasStore.getState().mode === 'learn') {
      setSidebarTab('explain');
    }
  }, [setSelectedNodeId, setSidebarTab]);

  const onEdgeClick = useCallback((_, edge) => {
    if (useCanvasStore.getState().mode === 'learn') return;
    setSelectedEdgeId(edge.id);
    setShowSettingsPanel(true);
    closeSidebar();
  }, [setSelectedEdgeId, closeSidebar]);

  const onPaneClick = useCallback(() => {
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    closeSidebar();
    setShowSettingsPanel(false);
  }, [setSelectedNodeId, setSelectedEdgeId, closeSidebar]);

  const handleSave = async () => {
    const state = useCanvasStore.getState();
    try {
      const data = {
        title: state.mapTitle || 'Untitled Map',
        nodes: JSON.stringify(state.nodes),
        edges: JSON.stringify(state.edges),
        nodeChats: JSON.stringify(state.nodeChats),
        nodeMastery: JSON.stringify(state.nodeMastery),
      };
      if (state.mapId) {
        await api.put(`/maps/${state.mapId}`, data);
      } else {
        const res = await api.post('/maps', data);
        loadMap(res.data.id, state.mapTitle, state.nodes, state.edges, state.nodeChats, state.nodeMastery);
      }
      setDirty(false);
      toast.success('Map saved');
      refreshUser();
    } catch (err) {
      if (err.response?.status === 403) {
        const detail = err.response?.data?.detail;
        if (detail === 'NO_CREDITS') {
          toast.error('No credits remaining. Upgrade for more credits.');
        } else if (detail === 'MAP_LIMIT_REACHED') {
          toast.error('Map limit reached. Upgrade to PRO for unlimited maps.');
        } else {
          toast.error('Limit reached. Upgrade your plan.');
        }
        openUpgradeModal();
      }
      else toast.error('Failed to save');
    }
  };

  const handleDeleteEdge = () => {
    if (selectedEdgeId) {
      deleteEdge(selectedEdgeId);
    }
  };

  const isPro = user?.plan === 'PRO' || user?.plan === 'MAX';

  const handleExportPNG = async () => {
    if (!isPro) { openUpgradeModal(); return; }
    if (nodes.length === 0) { toast.error('No content to export'); return; }
    setExporting(true);
    
    try {
      const { toSvg, toPng } = await import('html-to-image');
      
      // Get the ReactFlow container
      const element = document.querySelector('.react-flow');
      if (!element) throw new Error('No ReactFlow element');
      
      // Capture the visible area at high resolution
      const dataUrl = await toPng(element, {
        backgroundColor: canvasBackground,
        quality: 1,
        pixelRatio: 3,
        filter: (node) => {
          // Exclude controls and minimap from export
          const exclusions = ['react-flow__minimap', 'react-flow__controls', 'react-flow__attribution', 'react-flow__panel'];
          if (node.classList) {
            return !exclusions.some(c => node.classList.contains(c));
          }
          return true;
        },
        cacheBust: true,
        includeQueryParams: true,
      });
      
      const link = document.createElement('a');
      link.download = `${mapTitle || 'atlas-map'}.png`;
      link.href = dataUrl;
      link.click();
      toast.success('PNG exported successfully');
    } catch (err) { 
      console.error('PNG export error:', err);
      toast.error('Export failed'); 
    }
    finally { setExporting(false); }
  };

  const handleExportPDF = async () => {
    if (!isPro) { openUpgradeModal(); return; }
    if (nodes.length === 0) { toast.error('No content to export'); return; }
    setExporting(true);
    
    try {
      const { toPng } = await import('html-to-image');
      const { default: jsPDF } = await import('jspdf');
      
      // Get the ReactFlow container
      const element = document.querySelector('.react-flow');
      if (!element) throw new Error('No ReactFlow element');
      
      // Capture the visible area at high resolution
      const pixelRatio = 3;
      const dataUrl = await toPng(element, {
        backgroundColor: canvasBackground,
        quality: 1,
        pixelRatio: pixelRatio,
        filter: (node) => {
          // Exclude controls and minimap from export
          const exclusions = ['react-flow__minimap', 'react-flow__controls', 'react-flow__attribution', 'react-flow__panel'];
          if (node.classList) {
            return !exclusions.some(c => node.classList.contains(c));
          }
          return true;
        },
        cacheBust: true,
        includeQueryParams: true,
      });
      
      // Create image to get dimensions
      const img = new Image();
      img.src = dataUrl;
      await new Promise(resolve => { img.onload = resolve; });
      
      // Calculate PDF dimensions (convert px to mm)
      const imgWidth = img.width / pixelRatio;
      const imgHeight = img.height / pixelRatio;
      const pxToMm = 0.264583;
      const pdfWidth = imgWidth * pxToMm;
      const pdfHeight = imgHeight * pxToMm;
      
      // Create PDF
      const orientation = pdfWidth > pdfHeight ? 'landscape' : 'portrait';
      const pdf = new jsPDF({ 
        orientation, 
        unit: 'mm', 
        format: [pdfWidth, pdfHeight],
        compress: true
      });
      
      pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      pdf.save(`${mapTitle || 'atlas-map'}.pdf`);
      toast.success('PDF exported successfully');
    } catch (err) { 
      console.error('PDF export error:', err);
      toast.error('Export failed'); 
    }
    finally { setExporting(false); }
  };

  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const selectedEdge = edges.find(e => e.id === selectedEdgeId);

  return (
    <div className="flex h-screen overflow-hidden" data-testid="canvas-page" onMouseMove={handleCollabMouseMove}>
      {/* Canvas Area */}
      <div className="flex-1 relative" style={{ background: canvasBackground }}>
        {/* Toolbar */}
        <div className="absolute top-0 left-0 right-0 z-10 flex items-center px-4 py-2 bg-white/90 backdrop-blur-sm border-b border-border" data-testid="canvas-toolbar">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button onClick={() => navigate('/dashboard')} className="p-1.5 hover:bg-muted rounded-md transition-colors" data-testid="canvas-back-btn">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <div className="h-5 w-px bg-border" />
            <img src="/atlas-logo.png" alt="Atlas" className="w-4 h-4 object-contain" />
            <input
              value={mapTitle}
              onChange={e => setMapTitle(e.target.value)}
              placeholder="Untitled Map"
              className="font-body text-sm font-medium bg-transparent outline-none border-0 w-48"
              data-testid="canvas-title-input"
            />
          </div>

          {/* Center: Mode Toggle */}
          <div className="flex items-center justify-center">
            <div className="flex items-center border border-border rounded-md overflow-hidden" data-testid="mode-toggle">
              <button
                onClick={() => useCanvasStore.getState().setMode('edit')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-body transition-colors ${mode === 'edit' ? 'bg-cobalt text-white' : 'hover:bg-muted'}`}
                data-testid="mode-edit-btn"
              >
                <Pencil className="w-3 h-3" strokeWidth={1.5} /> Edit
              </button>
              <button
                onClick={() => useCanvasStore.getState().setMode('learn')}
                className={`flex items-center gap-1 px-3 py-1.5 text-xs font-body transition-colors ${mode === 'learn' ? 'bg-cobalt text-white' : 'hover:bg-muted'}`}
                data-testid="mode-learn-btn"
              >
                <BookOpen className="w-3 h-3" strokeWidth={1.5} /> Learn
              </button>
            </div>
          </div>

          {/* Right: Save with auto-save status */}
          <div className="flex items-center gap-2 flex-1 justify-end">
            {/* Auto-save status indicator */}
            <div className="flex items-center gap-1.5 text-[10px] font-mono text-muted-foreground">
              {autoSaveStatus === 'saving' && (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  <span>Saving...</span>
                </>
              )}
              {autoSaveStatus === 'saved' && !isDirty && mapId && (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  <span>Saved</span>
                </>
              )}
              {autoSaveStatus === 'error' && (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  <span>Error</span>
                </>
              )}
            </div>
            
            {/* Collaborator avatars - show only when there are participants */}
            {mapId && collabConnected && participants.length > 0 && (
              <div className="flex items-center gap-2">
                <CollaboratorAvatars participants={participants} />
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-green-50 text-green-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  {participants.length} live
                </div>
              </div>
            )}
            
            {/* Share button */}
            {mapId && (
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => setShowShareModal(true)}
                className="font-body text-xs h-7 px-3"
                data-testid="share-btn"
              >
                <Share2 className="w-3 h-3 mr-1" strokeWidth={1.5} />
                Share
              </Button>
            )}
            
            <Button size="sm" onClick={handleSave} data-testid="save-btn"
              className="bg-cobalt text-white hover:bg-cobalt/90 font-body text-xs h-7 px-3 btn-press">
              <Save className="w-3 h-3 mr-1" strokeWidth={1.5} />
              Save{isDirty ? ' *' : ''}
            </Button>
          </div>
        </div>

        {/* ReactFlow Canvas Area */}
        <div className="absolute inset-0 pt-12" data-testid="canvas-area">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onReconnect={onReconnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            onNodeDrag={handleCollabNodeDrag}
            onNodeDragStop={handleCollabNodeDragStop}
            edgesReconnectable={mode === 'edit'}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            minZoom={0.1}
            maxZoom={2}
            className="w-full h-full"
          >
            {mode === 'edit' && <Background variant="dots" color="#b8b8b8" gap={24} size={2} />}
            <Controls showInteractive={false} className="!bottom-4 !left-4" />
            <MiniMap
              nodeColor={(node) => {
                const mastery = node.data?.mastery || 'none';
                if (mastery === 'mastered') return '#10B981';
                if (mastery === 'competent') return '#0047AB';
                if (mastery === 'learning') return '#F59E0B';
                return '#E2E2E2';
              }}
              className="!bottom-4 !right-4 border border-border !rounded-md"
              maskColor="rgba(0,0,0,0.05)"
            />
          </ReactFlow>
        </div>

        {/* Remote cursors overlay - show collaborator cursors */}
        <CollabCursors cursors={cursors} />

        {/* Top-right canvas controls - Edit mode only */}
        {mode === 'edit' && (
          <div className="absolute top-14 right-4 z-10 flex flex-col gap-1.5 mt-2" data-testid="canvas-controls-right">
            <button
              ref={settingsButtonRef}
              onClick={() => setShowSettingsPanel(p => !p)}
              className={`w-9 h-9 flex items-center justify-center rounded-md border shadow-sm transition-colors ${
                showSettingsPanel ? 'bg-cobalt text-white border-cobalt' : 'bg-white border-border hover:bg-muted'
              }`}
              data-testid="settings-panel-btn"
              title="Canvas Settings"
            >
              <Paintbrush className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        )}

        {/* Export buttons - top-right below settings, always visible */}
        <div className={`absolute ${mode === 'edit' ? 'top-[100px]' : 'top-14 mt-2'} right-4 z-10 flex flex-col gap-1.5`} data-testid="export-controls">
          <button
            onClick={handleExportPNG}
            disabled={exporting}
            className={`w-9 h-9 flex items-center justify-center rounded-md border shadow-sm transition-colors relative ${
              isPro ? 'bg-white border-border hover:bg-muted' : 'bg-white border-border opacity-70 cursor-not-allowed'
            }`}
            title={isPro ? 'Export as PNG' : 'Export PNG (Pro only)'}
            data-testid="export-png-btn"
          >
            <FileImage className="w-4 h-4" strokeWidth={1.5} />
            {!isPro && <Lock className="w-2.5 h-2.5 absolute -top-1 -right-1 text-orange bg-white rounded-full" strokeWidth={2.5} />}
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className={`w-9 h-9 flex items-center justify-center rounded-md border shadow-sm transition-colors relative ${
              isPro ? 'bg-white border-border hover:bg-muted' : 'bg-white border-border opacity-70 cursor-not-allowed'
            }`}
            title={isPro ? 'Export as PDF' : 'Export PDF (Pro only)'}
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4" strokeWidth={1.5} />
            {!isPro && <Lock className="w-2.5 h-2.5 absolute -top-1 -right-1 text-orange bg-white rounded-full" strokeWidth={2.5} />}
          </button>
        </div>
      </div>

      {/* ── Right Panel: Learn Sidebar ── */}
      {selectedNode && sidebarTab && (
        <div className="w-[380px] border-l border-border bg-white flex flex-col animate-slide-in-right" data-testid="canvas-sidebar">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-0.5">Selected Node</p>
              <h3 className="font-body text-sm font-medium truncate">{selectedNode.data.label}</h3>
            </div>
            <button onClick={closeSidebar} className="p-1 hover:bg-muted rounded-md" data-testid="close-sidebar-btn">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
          <div className="px-4 py-2 border-b border-border" data-testid="sidebar-tabs">
            <div className="flex gap-1">
              {[
                { id: 'explain', icon: Search, label: 'Explain' },
                { id: 'ask', icon: MessagesSquare, label: 'Ask' },
                { id: 'quiz', icon: GraduationCap, label: 'Check' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setSidebarTab(tab.id)}
                  className={`flex items-center gap-1 px-3 py-1.5 text-xs font-body rounded-md transition-colors ${
                    sidebarTab === tab.id ? 'bg-cobalt text-white' : 'hover:bg-muted text-muted-foreground'
                  }`}
                  data-testid={`sidebar-tab-${tab.id}`}
                >
                  <tab.icon className="w-3 h-3" strokeWidth={1.5} /> {tab.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {sidebarTab === 'explain' && <ExplainTab nodeLabel={selectedNode.data.label} nodeDescription={selectedNode.data.description} />}
            {sidebarTab === 'ask' && <AskTab nodeId={selectedNodeId} nodeLabel={selectedNode.data.label} nodeDescription={selectedNode.data.description} />}
            {sidebarTab === 'quiz' && <QuizTab nodeId={selectedNodeId} nodeLabel={selectedNode.data.label} nodeDescription={selectedNode.data.description} />}
          </div>
        </div>
      )}

      {/* ── Right Panel: Edge & Canvas Settings (Edit mode) ── */}
      {showSettingsPanel && mode === 'edit' && (
        <div ref={settingsPanelRef} className="w-[280px] border-l border-border bg-white flex flex-col animate-slide-in-right" data-testid="settings-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <Paintbrush className="w-4 h-4 text-cobalt" strokeWidth={1.5} />
              <span className="font-body text-sm font-medium">Settings</span>
            </div>
            <button onClick={() => setShowSettingsPanel(false)} className="p-1 hover:bg-muted rounded-md" data-testid="close-settings-panel-btn">
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>

          {/* Canvas Background */}
          <div className="px-4 py-4">
            <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground block mb-2.5">Canvas Background</span>
            <div className="grid grid-cols-6 gap-2">
              {BG_COLORS.map(c => (
                <button
                  key={c.value}
                  onClick={() => setCanvasBackground(c.value)}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${
                    canvasBackground === c.value ? 'border-cobalt scale-110 shadow-sm' : 'border-border hover:border-foreground/30'
                  }`}
                  style={{ background: c.value }}
                  title={c.name}
                  data-testid={`bg-color-${c.name.toLowerCase()}`}
                />
              ))}
            </div>
          </div>

          {/* Branch Color — only when edge is selected */}
          {selectedEdge && (
            <>
              <div className="px-4 py-4 border-t border-border">
                <div className="flex items-center gap-1.5 mb-2.5">
                  <GitBranch className="w-3 h-3 text-cobalt" strokeWidth={1.5} />
                  <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-muted-foreground">Branch Color</span>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {EDGE_COLORS.map(c => (
                    <button
                      key={c.value}
                      onClick={() => updateEdgeColor(selectedEdgeId, c.value)}
                      className={`w-8 h-8 rounded-lg border-2 transition-all ${
                        selectedEdge?.style?.stroke === c.value ? 'border-foreground scale-110 shadow-sm' : 'border-transparent hover:border-border'
                      }`}
                      style={{ background: c.value }}
                      title={c.name}
                      data-testid={`edge-color-${c.name.toLowerCase()}`}
                    />
                  ))}
                </div>
              </div>

              <div className="px-4 py-3 border-t border-border">
                <button
                  onClick={handleDeleteEdge}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-body text-destructive hover:bg-destructive/5 border border-destructive/20 rounded-md transition-colors"
                  data-testid="delete-edge-btn"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Delete Connection
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Onboarding for first-time canvas users */}
      {showOnboarding && user && (
        <Onboarding
          steps={CANVAS_ONBOARDING_STEPS}
          onboardingKey="canvas"
          hasCompleted={user?.onboarding?.canvas}
          onComplete={() => setShowOnboarding(false)}
        />
      )}

      {/* Sidebar tabs onboarding - shows when user first clicks a node */}
      {showSidebarOnboarding && selectedNodeId && sidebarTab && user && (
        <Onboarding
          steps={[{
            target: '[data-testid="sidebar-tabs"]',
            title: 'AI Learning Tools',
            description: 'Use these tabs to learn about the selected topic: Explain for AI explanations, Ask to chat about it, and Check to test your knowledge with quizzes.',
            position: 'left'
          }]}
          onboardingKey="canvas_sidebar"
          hasCompleted={user?.onboarding?.canvas_sidebar}
          onComplete={() => setShowSidebarOnboarding(false)}
        />
      )}

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        mapId={mapId}
        mapTitle={mapTitle}
      />
    </div>
  );
}
